import React, { useEffect, useState, useCallback } from "react";
import {
	Button,
	Container,
	CssBaseline,
	Typography,
	TextField,
	Link,
	Grid,
	CircularProgress,
	IconButton,
	InputAdornment,
	Box,
	Checkbox,
	Paper,
	Fade,
	useMediaQuery,
} from "@mui/material";
import { createTheme, ThemeProvider, useTheme } from "@mui/material/styles";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import PersonIcon from "@mui/icons-material/Person";

import api from "../../utils/api";
import { trackEvent } from "../../utils/analytics";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { useLogger } from "../../hooks/Logger";

const theme = createTheme({
	palette: {
		primary: {
			main: "#6366F1",
			light: "rgba(99, 102, 241, 0.7)",
			dark: "#4F46E5",
		},
		secondary: {
			main: "#6366F1",
		},
	},
	typography: {
		fontFamily: '"Montserrat", "Roboto", "Helvetica", "Arial", sans-serif',
		h5: {
			fontWeight: 700,
			letterSpacing: "-0.5px",
		},
	},
	shape: {
		borderRadius: 16,
	},
	components: {
		MuiOutlinedInput: {
			styleOverrides: {
				input: {
					// Quitar el fondo azul que pone el navegador al autocompletar
					"&:-webkit-autofill": {
						WebkitBoxShadow: "0 0 0 100px #fff inset",
						boxShadow: "0 0 0 100px #fff inset",
					},
					"&:-webkit-autofill:hover": {
						WebkitBoxShadow: "0 0 0 100px #fff inset",
						boxShadow: "0 0 0 100px #fff inset",
					},
					"&:-webkit-autofill:focus": {
						WebkitBoxShadow: "0 0 0 100px #fff inset",
						boxShadow: "0 0 0 100px #fff inset",
					},
					"&:-webkit-autofill:active": {
						WebkitBoxShadow: "0 0 0 100px #fff inset",
						boxShadow: "0 0 0 100px #fff inset",
					},
				},
			},
		},
	},
});

const Auth = ({ setShowLogin, setJustInitSession, initialMode = "login" }) => {
	const log = useLogger();
	const [isLogin, setIsLogin] = useState(initialMode === "login");
	const [showPassword, setShowPassword] = React.useState(false);
	const [email, setEmail] = useState(null);
	const [name, setName] = useState(null);
	const [password, setPassword] = useState(null);
	const [repeatPassword, setRepeatPassword] = useState(null);
	const [acceptPolicy, setAcceptPolicy] = useState(false);

	const [isLoading, setIsLoading] = useState(false);
	const handleClickShowPassword = () => setShowPassword((show) => !show);

	const { enqueueSnackbar } = useSnackbar();
	const navigate = useNavigate();
	const muiTheme = useTheme();
	const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));

	const handleMouseDownPassword = (event) => {
		event.preventDefault();
	};

	const [errors, setErrors] = useState({
		email: "",
		name: "",
		password: "",
		repeatPassword: "",
	});

	const [touched, setTouched] = useState({
		email: false,
		name: false,
		password: false,
		repeatPassword: false,
	});

	const validateFields = useCallback((shouldShowErrors = false) => {
		const newErrors = {
			email: "",
			name: "",
			password: "",
			repeatPassword: "",
		};

		// Solo validar si el campo ha sido tocado o si estamos haciendo submit (shouldShowErrors)
		if (shouldShowErrors || touched.email) {
			if (!email) {
				newErrors.email = "El correo electrónico es obligatorio.";
			} else {
				const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
				if (!emailRegex.test(email)) {
					newErrors.email = "Por favor, introduce un correo electrónico válido.";
				}
			}
		}

		if (shouldShowErrors || touched.password) {
			if (!password) {
				newErrors.password = "La contraseña es obligatoria.";
			} else if (password.length < 6) {
				newErrors.password = "La contraseña tiene que tener al menos 6 caracteres";
			}
		}

		if (!isLogin) {
			if (shouldShowErrors || touched.name) {
				if (!name) {
					newErrors.name = "El nombre es obligatorio para el registro.";
				}
			}

			if (shouldShowErrors || touched.repeatPassword) {
				if (!repeatPassword) {
					newErrors.repeatPassword = "Debes repetir la contraseña.";
				} else if (password && password !== repeatPassword) {
					newErrors.repeatPassword = "Las contraseñas no coinciden.";
					if (shouldShowErrors || touched.password) {
						newErrors.password = "Las contraseñas no coinciden.";
					}
				}
			}
		}

		setErrors(newErrors);

		// Verificar si hay errores
		return !Object.values(newErrors).some(error => error !== "");
	}, [email, password, name, repeatPassword, isLogin, touched]);

	const handleSwitchMode = () => {
		setIsLogin((prev) => !prev);
		// Reset touched states al cambiar de modo
		setTouched({
			email: false,
			name: false,
			password: false,
			repeatPassword: false,
		});
		setErrors({
			email: "",
			name: "",
			password: "",
			repeatPassword: "",
		});
	};

	// Solo validar en tiempo real si el campo ha sido tocado
	useEffect(() => {
		validateFields(false);
	}, [email, password, name, repeatPassword, isLogin, touched]);

	const handleSubmit = async (e) => {
		e.preventDefault();
		// Marcar todos los campos como touched al intentar enviar
		setTouched({
			email: true,
			name: true,
			password: true,
			repeatPassword: true,
		});
		// Validar mostrando todos los errores
		if (!validateFields(true)) {
			return;
		}
		setIsLoading(true);

		try {
			if (isLogin) {
				// Login
				log.info("Auth: Iniciando sesión", { email });
				const response = await api.post("/auth/login", {
					email,
					password,
				});
				api.setToken(response.token);
				trackEvent("login", { method: "email" });
				// Recargar la página para actualizar el contexto
				window.location.reload();
			} else {
				// Register
				const response = await api.post("/auth/register", {
					email,
					password,
					displayName: name?.trim() || undefined,
				});
				api.setToken(response.token);
				trackEvent("sign_up", { method: "email" });
				// Recargar la página para actualizar el contexto
				window.location.reload();
			}
			if (setJustInitSession) {
				setJustInitSession(true);
			}
			setShowLogin(false);
		} catch (error) {
			log.error("Auth: error al iniciar sesión", {
				message: error?.message,
			});
			enqueueSnackbar(
				error.message || "Error al iniciar sesión. Intenta de nuevo.",
				{ variant: "error" }
			);
		} finally {
			setIsLoading(false);
		}
	};


	return (
		<ThemeProvider theme={theme}>
			<Box
				sx={{
					width: "100%",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					position: "relative",
				}}
			>
				<Container
					component="main"
					maxWidth="xs"
					sx={{ position: "relative", zIndex: 1, width: "100%" }}
				>
					<CssBaseline />
					<Fade in={true} timeout={800}>
						<Paper
							elevation={0}
							sx={{
								padding: { xs: 1.5, sm: 2.5 },
								borderRadius: 4,
								background: "#fff",
								boxShadow: "none",
								width: "100%",
							}}
						>
							<Box sx={{ textAlign: "center", mb: isMobile ? 2 : 3 }}>
								<FlightTakeoffIcon
									sx={{
										fontSize: isMobile ? 32 : 40,
										color: "primary.main",
										mb: isMobile ? 1 : 1.5,
										animation: "float 3s ease-in-out infinite",
										"@keyframes float": {
											"0%, 100%": { transform: "translateY(0px)" },
											"50%": { transform: "translateY(-10px)" },
										},
									}}
								/>
								<Typography
									component="h1"
									variant="h5"
									sx={{
										fontWeight: 700,
										background:
											"linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
										backgroundClip: "text",
										WebkitBackgroundClip: "text",
										WebkitTextFillColor: "transparent",
										mb: isMobile ? 0.5 : 1,
										fontSize: isMobile ? "1.25rem" : "1.5rem",
									}}
								>
									{isLogin ? "Bienvenido de vuelta" : "Crea tu cuenta"}
								</Typography>
								{!isMobile && (
									<Typography
										variant="body2"
										sx={{
											color: "text.secondary",
											fontSize: "0.8rem",
											mb: 0.5,
										}}
									>
										{isLogin
											? "Continúa creando tus guías de viaje"
											: "Comienza a crear tus guías de viaje únicas"}
									</Typography>
								)}
							</Box>
							<form onSubmit={handleSubmit}>
								<Grid container spacing={isMobile ? 1.5 : 2}>
									{!isLogin && (
										<Grid item xs={12}>
											<TextField
												fullWidth
												id="name"
												label="Nombre"
												value={name || ""}
												placeholder="Tu nombre"
												onChange={(e) => {
													setName(e.target.value);
												}}
												onBlur={() => {
													setTouched((prev) => ({ ...prev, name: true }));
												}}
												error={!!errors.name}
												helperText={errors.name}
												InputLabelProps={{
													sx: {
														color: "#64748B",
														fontWeight: 500,
														fontSize: "0.875rem",
														"&.Mui-focused": { color: "#6366F1" },
														"&.Mui-error": { color: "#DC2626" },
													},
												}}
												FormHelperTextProps={{
													sx: { fontSize: "0.75rem", mt: 0.5 },
												}}
												InputProps={{
													startAdornment: (
														<InputAdornment position="start">
															<PersonIcon sx={{ color: "text.secondary" }} />
														</InputAdornment>
													),
												}}
												sx={{
													"& .MuiOutlinedInput-root": {
														borderRadius: "12px",
														transition: "all 0.2s ease",
														"&:hover": {
															"& .MuiOutlinedInput-notchedOutline": {
																borderColor: "rgba(99, 102, 241, 0.5)",
															},
														},
														"&.Mui-focused": {
															"& .MuiOutlinedInput-notchedOutline": {
																borderColor: "#6366F1",
																borderWidth: "1.5px",
															},
														},
														"&.Mui-error": {
															"& .MuiOutlinedInput-notchedOutline": {
																borderColor: "rgba(220, 38, 38, 0.7)",
															},
														},
													},
												}}
											/>
										</Grid>
									)}
									<Grid item xs={12}>
										<TextField
											fullWidth
											id="email"
											label="Correo electrónico"
											type="email"
											value={email || ""}
											placeholder="tu@email.com"
											onChange={(e) => {
												setEmail(e.target.value);
											}}
											onBlur={() => {
												setTouched((prev) => ({ ...prev, email: true }));
											}}
											error={!!errors.email}
											helperText={errors.email}
											InputLabelProps={{
												sx: {
													color: "#64748B",
													fontWeight: 500,
													fontSize: "0.875rem",
													"&.Mui-focused": { color: "#6366F1" },
													"&.Mui-error": { color: "#DC2626" },
												},
											}}
											FormHelperTextProps={{
												sx: { fontSize: "0.75rem", mt: 0.5 },
											}}
											InputProps={{
												startAdornment: (
													<InputAdornment position="start">
														<EmailIcon sx={{ color: "text.secondary" }} />
													</InputAdornment>
												),
											}}
											sx={{
												"& .MuiOutlinedInput-root": {
													borderRadius: "12px",
													transition: "all 0.2s ease",
													"&:hover": {
														"& .MuiOutlinedInput-notchedOutline": {
															borderColor: "rgba(99, 102, 241, 0.5)",
														},
													},
													"&.Mui-focused": {
														"& .MuiOutlinedInput-notchedOutline": {
															borderColor: "#6366F1",
															borderWidth: "1.5px",
														},
													},
													"&.Mui-error": {
														"& .MuiOutlinedInput-notchedOutline": {
															borderColor: "rgba(220, 38, 38, 0.7)",
														},
													},
												},
											}}
										/>
									</Grid>

									<Grid item xs={12}>
										<TextField
											fullWidth
											id="password"
											label="Contraseña"
											type={showPassword ? "text" : "password"}
											value={password || ""}
											placeholder="Mínimo 6 caracteres"
											onChange={(e) => {
												setPassword(e.target.value);
											}}
											onBlur={() => {
												setTouched((prev) => ({ ...prev, password: true }));
											}}
											error={!!errors.password}
											helperText={errors.password}
											InputLabelProps={{
												sx: {
													color: "#64748B",
													fontWeight: 500,
													fontSize: "0.875rem",
													"&.Mui-focused": { color: "#6366F1" },
													"&.Mui-error": { color: "#DC2626" },
												},
											}}
											FormHelperTextProps={{
												sx: { fontSize: "0.75rem", mt: 0.5 },
											}}
											InputProps={{
												startAdornment: (
													<InputAdornment position="start">
														<LockIcon sx={{ color: "text.secondary" }} />
													</InputAdornment>
												),
												endAdornment: (
													<InputAdornment position="end">
														<IconButton
															aria-label="toggle password visibility"
															onClick={handleClickShowPassword}
															onMouseDown={handleMouseDownPassword}
															edge="end"
															sx={{ color: "text.secondary" }}
														>
															{showPassword ? (
																<VisibilityOff />
															) : (
																<Visibility />
															)}
														</IconButton>
													</InputAdornment>
												),
											}}
											sx={{
												"& .MuiOutlinedInput-root": {
													borderRadius: "12px",
													transition: "all 0.2s ease",
													"&:hover": {
														"& .MuiOutlinedInput-notchedOutline": {
															borderColor: "rgba(99, 102, 241, 0.5)",
														},
													},
													"&.Mui-focused": {
														"& .MuiOutlinedInput-notchedOutline": {
															borderColor: "#6366F1",
															borderWidth: "1.5px",
														},
													},
													"&.Mui-error": {
														"& .MuiOutlinedInput-notchedOutline": {
															borderColor: "rgba(220, 38, 38, 0.7)",
														},
													},
												},
											}}
										/>
									</Grid>
									{!isLogin && (
										<Grid item xs={12}>
											<TextField
												fullWidth
												id="repeatPassword"
												label="Repite la contraseña"
												type={showPassword ? "text" : "password"}
												value={repeatPassword || ""}
												placeholder="Confirma tu contraseña"
												onChange={(e) => {
													setRepeatPassword(e.target.value);
												}}
												onBlur={() => {
													setTouched((prev) => ({ ...prev, repeatPassword: true }));
												}}
												error={!!errors.repeatPassword}
												helperText={errors.repeatPassword}
												InputLabelProps={{
													sx: {
														color: "#64748B",
														fontWeight: 500,
														fontSize: "0.875rem",
														"&.Mui-focused": { color: "#6366F1" },
														"&.Mui-error": { color: "#DC2626" },
													},
												}}
												FormHelperTextProps={{
													sx: { fontSize: "0.75rem", mt: 0.5 },
												}}
												InputProps={{
													startAdornment: (
														<InputAdornment position="start">
															<LockIcon sx={{ color: "text.secondary" }} />
														</InputAdornment>
													),
													endAdornment: (
														<InputAdornment position="end">
															<IconButton
																aria-label="toggle password visibility"
																onClick={handleClickShowPassword}
																onMouseDown={handleMouseDownPassword}
																edge="end"
																sx={{ color: "text.secondary" }}
															>
																{showPassword ? (
																	<VisibilityOff />
																) : (
																	<Visibility />
																)}
															</IconButton>
														</InputAdornment>
													),
												}}
												sx={{
													"& .MuiOutlinedInput-root": {
														borderRadius: "12px",
														transition: "all 0.2s ease",
														"&:hover": {
															"& .MuiOutlinedInput-notchedOutline": {
																borderColor: "rgba(99, 102, 241, 0.5)",
															},
														},
														"&.Mui-focused": {
															"& .MuiOutlinedInput-notchedOutline": {
																borderColor: "#6366F1",
																borderWidth: "1.5px",
															},
														},
														"&.Mui-error": {
															"& .MuiOutlinedInput-notchedOutline": {
																borderColor: "rgba(220, 38, 38, 0.7)",
															},
														},
													},
												}}
											/>
										</Grid>
									)}
									{isLogin && (
										<Grid item xs={12} sx={{ mt: -0.5, mb: 0 }}>
											<Link
												component="button"
												type="button"
												variant="body2"
												onClick={() => navigate("/forgot-password")}
												sx={{
													color: "primary.main",
													fontSize: isMobile ? "0.8rem" : "0.875rem",
													textDecoration: "none",
													"&:hover": { textDecoration: "underline" },
													cursor: "pointer",
												}}
											>
												¿Olvidaste tu contraseña?
											</Link>
										</Grid>
									)}
								</Grid>
								{!isLogin && (
									<Box
										sx={{
											display: "flex",
											alignItems: "flex-start",
											gap: isMobile ? 1 : 1.5,
											mt: isMobile ? 1 : 1.5,
											mb: isMobile ? 1 : 1.5,
										}}
									>
										<Checkbox
											checked={acceptPolicy}
											onChange={(event) =>
												setAcceptPolicy(event.target.checked)
											}
											sx={{
												color: "primary.main",
												"&.Mui-checked": {
													color: "primary.main",
												},
												mt: -0.5,
											}}
										/>
										<Typography
											variant="body2"
											sx={{
												fontSize: isMobile ? "0.7rem" : "0.75rem",
												color: "text.secondary",
												lineHeight: 1.6,
											}}
										>
											He leído y acepto la{" "}
											<Link
												component={RouterLink}
												to="/legal/privacidad"
												rel="nofollow"
												sx={{
													color: "primary.main",
													textDecoration: "none",
													"&:hover": { textDecoration: "underline" },
												}}
											>
												Política de Privacidad
											</Link>
											, los{" "}
											<Link
												component={RouterLink}
												to="/legal/terminos"
												rel="nofollow"
												sx={{
													color: "primary.main",
													textDecoration: "none",
													"&:hover": { textDecoration: "underline" },
												}}
											>
												Términos y Condiciones
											</Link>{" "}
											y la{" "}
											<Link
												component={RouterLink}
												to="/legal/cookies"
												rel="nofollow"
												sx={{
													color: "primary.main",
													textDecoration: "none",
													"&:hover": { textDecoration: "underline" },
												}}
											>
												Política de Cookies
											</Link>
										</Typography>
									</Box>
								)}
								<Button
									type="submit"
									fullWidth
									variant="contained"
									color="primary"
									disabled={
										errors.email ||
										errors.name ||
										errors.password ||
										errors.repeatPassword ||
										(!acceptPolicy && !isLogin) ||
										isLoading
									}
									sx={{
										mt: isMobile ? 1.5 : 2,
										mb: isMobile ? 1 : 1.5,
										py: 1,
										px: 2.5,
										borderRadius: "12px",
										fontSize: "0.875rem",
										fontWeight: 600,
										textTransform: "none",
										backgroundColor: "#6366F1 !important",
										color: "#fff !important",
										boxShadow: "0 2px 8px rgba(99, 102, 241, 0.3)",
										transition: "all 0.2s ease",
										"&:hover": {
											backgroundColor: "#4F46E5 !important",
											boxShadow: "0 4px 12px rgba(99, 102, 241, 0.4)",
										},
										"&:disabled": {
											background: "rgba(0, 0, 0, 0.12) !important",
											color: "rgba(0, 0, 0, 0.26) !important",
										},
									}}
								>
									{isLoading ? (
										<CircularProgress size={24} sx={{ color: "white" }} />
									) : isLogin ? (
										"Iniciar sesión"
									) : (
										"Crear cuenta"
									)}
								</Button>
							</form>

							<Box
								sx={{
									textAlign: "center",
									pt: isMobile ? 1 : 1.5,
									mt: isMobile ? 1 : 1.5,
									borderTop: "1px solid rgba(0, 0, 0, 0.08)",
								}}
							>
								<Typography
									variant="body2"
									sx={{
										color: "text.secondary",
										fontSize: isMobile ? "0.8rem" : "0.875rem",
										mb: isMobile ? 0.75 : 1,
									}}
								>
									{isLogin
										? "¿No tienes una cuenta?"
										: "¿Ya tienes una cuenta?"}
								</Typography>
								<Link
									component="button"
									variant="body2"
									onClick={handleSwitchMode}
									sx={{
										color: "primary.main",
										fontWeight: 600,
										textDecoration: "none",
										fontSize: isMobile ? "0.8rem" : "0.875rem",
										cursor: "pointer",
										transition: "all 0.2s ease",
										"&:hover": {
											color: "primary.dark",
											textDecoration: "underline",
										},
									}}
								>
									{isLogin ? "Regístrate aquí" : "Inicia sesión aquí"}
								</Link>
							</Box>
						</Paper>
					</Fade>
				</Container>
			</Box>
		</ThemeProvider>
	);
};

export default Auth;
